// src/components/ProtectedRoute.js

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { loadUser, logoutUser } from "../actions/userActions";

const ProtectedRoute = ({ children, role }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { loading, isAuthenticated, user } = useSelector((state) => state.user);

  // track whether we've tried loading the user
  const triedToLoadUser = useRef(false);
  // we only render after our initial auth check
  const [checkedAuth, setCheckedAuth] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");

    if (token && !isAuthenticated && !triedToLoadUser.current) {
      triedToLoadUser.current = true;
      dispatch(loadUser())
        .catch(() => dispatch(logoutUser()))
        .finally(() => setCheckedAuth(true));
    } else {
      // no token or already authenticated
      setCheckedAuth(true);
    }
  }, [dispatch, isAuthenticated]);

  // show spinner while we’re checking auth
  if (loading || !checkedAuth) {
    return (
      <div style={{ textAlign: "center", marginTop: 60 }}>
        Loading…
      </div>
    );
  }

  // not logged in?
  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    );
  }

  // role-based guard
  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    if (!allowed.includes(user.role)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // redirect based on user.role if they navigate somewhere disallowed
  const { pathname } = location;

  if (user.role === "admin") {
    const adminPaths = [
      "/franchise",
      "/franchise/create",
      "/adminconsole",
      "/sales",
      "/stock",
      "/business-insights",
      "/user",
    ];
    if (!adminPaths.some((p) => pathname.startsWith(p))) {
      return <Navigate to="/franchise" replace />;
    }
  }

  if (user.role === "sale" && !pathname.startsWith("/sales")) {
    return <Navigate to="/sales" replace />;
  }
  if (user.role === "stock" && !pathname.startsWith("/stock")) {
    return <Navigate to="/stock" replace />;
  }
  if (
    user.role === "profit-loss" &&
    !pathname.startsWith("/business-insights")
  ) {
    return <Navigate to="/business-insights" replace />;
  }

  // all good — render children
  return <>{children}</>;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  role: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

export default ProtectedRoute;
